import { AcademyModeService } from '../../core/services/academy-mode.service';
import {
  Component,
  computed,
  effect,
  input,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { PatientPlaylistModel, PlaylistOrderModel } from '../../core/api/models';
import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { SupervisorBaseComponent } from '../supervisor-base.component';
import { AlertService } from '../../shared/services/alert.service';
import { SelectItem } from 'primeng/api';
import { SelectItemGroup } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';

// Interface to define the structure of expansion states
interface ExpansionState {
  playlists: { [key: number]: boolean };
  programs: { [key: number]: boolean };
  targets: { [key: number]: boolean };
}

@Component({
  selector: 'app-playlists',
  templateUrl: './playlists.component.html',
  styleUrl: '../supervisor-main.component.css',
  standalone: false,
})
export class PlaylistsComponent extends SupervisorBaseComponent {
  patientID = input.required<string>();
  selectedAvailabilityMode = signal<number | null>(null);
  get modeAvailabilityOptions() { return [0, 1, 2, 3].map(value => ({ value, label: this.modeFilterService.getModeDisplayNameById(value) })); }

  selectedFilters = signal<any[]>([]);
  
  // UI State
  filtersCollapsed = signal<boolean>(true);
  
  // Selection functionality
  selectingForDeletion: boolean = false;
  playlistIDsForDeletion: number[] = [];
  doSelectAll: boolean = false;
  
  // Order functionality
  orderMode = signal<'alphabetical' | 'custom'>('alphabetical');
  isEditMode = signal<boolean>(false);
  pendingOrderChanges = signal<PatientPlaylistModel[]>([]);
  lastMovedPlaylistId = signal<number | null>(null);
  
  orderOptions = signal<SelectItem[]>([
    { label: 'Alphabetically', value: 'alphabetical' },
    { label: 'Custom', value: 'custom' }
  ]);
  
  possibleFilters = computed<SelectItemGroup[]>(() => {
    let programs = this.supervisorHelperService.programs();
    let targets = this.supervisorHelperService.targets();
    return [
      {
        label: 'Programs',
        items: programs.map((program) => {
          return {
            label: program.ProgramName ?? 'No Name for Program',
            value: program,
          };
        }),
      },
      {
        label: 'Targets',
        items: targets.map((target) => {
          return {
            label: target.TargetName ?? 'No Name for Target',
            value: target,
          };
        }),
      },
    ];
  });
  searchKeyword = signal<string | null>(null);

  includeDeleted = signal<boolean>(false);

  filteredPlaylists = computed(() => {
    let filteredPlaylists = this.supervisorHelperService.playlists();
    const availabilityMode = this.selectedAvailabilityMode();
    if (availabilityMode != null) filteredPlaylists = filteredPlaylists.filter(x => x.SharedSparkModeIDs?.includes(availabilityMode));
    // use keyword from search bar for the first filter
    if (this.searchKeyword() != null && this.searchKeyword() != '') {
      filteredPlaylists = filteredPlaylists.filter((playlist) =>
        playlist.PlaylistName?.toLowerCase().includes(
          this.searchKeyword()!.toLowerCase()
        )
      );
    }
    this.selectedFilters().forEach((selectedFilter) => {
      if (this.supervisorHelperService.isPatientProgramModel(selectedFilter)) {
        filteredPlaylists = filteredPlaylists.filter((playlist) =>
          playlist.Items?.some(
            (item) => item.PatientProgramID === selectedFilter.ID
          )
        );
      }
      if (this.supervisorHelperService.isPatientTargetModel(selectedFilter)) {
        filteredPlaylists = filteredPlaylists.filter((playlist) =>
          playlist.Items?.some(
            (item) => item.PatientTargetID === selectedFilter.ID
          )
        );
      }
    });
    if (!this.includeDeleted()) {
      filteredPlaylists = filteredPlaylists.filter(
        (playlist) => !playlist.IsDeleted
      );
    }

    // Apply sorting based on order mode
    if (this.orderMode() === 'alphabetical') {
      // order playlists alphabetically
      filteredPlaylists.sort((a, b) => {
        return a.PlaylistName?.localeCompare(b.PlaylistName ?? '') ?? 0;
      });
    } else if (this.orderMode() === 'custom') {
      // order playlists by OrderNr
      filteredPlaylists.sort((a, b) => {
        const aOrder = a.OrderNr ?? 0;
        const bOrder = b.OrderNr ?? 0;
        return aOrder - bOrder;
      });
    }

    return filteredPlaylists;
  });

  // Enhanced expansion state management
  expansionState: ExpansionState = {
    playlists: {},
    programs: {},
    targets: {}
  };

  // Legacy property for backward compatibility
  get isExpandedStatus(): { [key: number]: boolean } {
    return this.expansionState.playlists;
  }

  onPlaylistExpandedChange(playlistId: number, isExpanded: boolean) {
    this.expansionState.playlists[playlistId] = isExpanded;
  }

  onProgramExpandedChange(programId: number, isExpanded: boolean) {
    this.expansionState.programs[programId] = isExpanded;
  }

  onTargetExpandedChange(targetId: number, isExpanded: boolean) {
    this.expansionState.targets[targetId] = isExpanded;
  }

  constructor(
    public modeFilterService: AcademyModeService,
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private router: Router,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
    effect(() => {
      console.log(
        'update signal triggered: ',
        this.supervisorHelperService.updateSignal()
      );

      // Save the current expansion states before the update
      const currentExpansionState: ExpansionState = {
        playlists: { ...this.expansionState.playlists },
        programs: { ...this.expansionState.programs },
        targets: { ...this.expansionState.targets }
      };

      if (this.patientID()) {
        if (!this.isLoading) {
          this.loadBaseData(Number(this.patientID()), () => {
            // Restore the expansion states after data is loaded
            this.expansionState = currentExpansionState;
          });
        }
      } else {
        this.router.navigate(['/select-patient']);
      }
    });

  }

  ngOnInit(): void {
    this.loadOrderModePreference();
  }

  clearSearch() {
    this.selectedAvailabilityMode.set(null);
    this.searchKeyword.set(null);
    this.selectedFilters.set([]);
    this.exitEditModeIfActive();
  }

  toggleIncludeDeleted() {
    this.includeDeleted.set(!this.includeDeleted());
    this.exitEditModeIfActive();
  }

  exitEditModeIfActive() {
    if (this.isEditMode()) {
      this.isEditMode.set(false);
      this.pendingOrderChanges.set([]);
    }
  }

  addPlaylistButtonClicked() {
    console.log('addPlaylistButtonClicked');
    this.supervisorHelperService.additPlaylistButtonClicked(null);
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed.set(!this.filtersCollapsed());
  }

  // Order functionality methods
  onOrderModeChange() {
    // Save the order mode preference
    this.saveOrderModePreference();
  }

  toggleEditMode() {
    if (this.isEditMode()) {
      // Exiting edit mode - save all pending changes
      this.saveAllPendingChanges();
    } else {
      // Starting edit mode - check if filters are active
      if (this.hasActiveFilters()) {
        this.alertService.confirm(
          'Clear Filters',
          'To start configuring a custom order for this list we must first clear the filters, click "yes" to reset the filters and start editing, "no" to cancel'
        ).subscribe((result) => {
          if (result) {
            this.clearAllFilters();
            this.isEditMode.set(true);
          }
        });
        return;
      }
    }
    this.isEditMode.set(!this.isEditMode());
  }

  private hasActiveFilters(): boolean {
    return (
      (this.selectedFilters() && this.selectedFilters().length > 0) ||
      (this.searchKeyword() && this.searchKeyword() !== '') ||
      this.includeDeleted()
    );
  }

  private clearAllFilters(): void {
    this.selectedFilters.set([]);
    this.searchKeyword.set(null);
    this.includeDeleted.set(false);
  }

  movePlaylistUp(playlistId: number) {
    if (this.orderMode() === 'custom') {
      const playlists = this.filteredPlaylists();
      const currentIndex = playlists.findIndex(p => p.ID === playlistId);
      
      if (currentIndex > 0) {
        // Track the last moved playlist
        this.lastMovedPlaylistId.set(playlistId);
        
        // Swap with previous playlist
        const temp = playlists[currentIndex];
        playlists[currentIndex] = playlists[currentIndex - 1];
        playlists[currentIndex - 1] = temp;
        
        // Update OrderNr for all playlists
        playlists.forEach((p, index) => {
          p.OrderNr = index + 1;
        });
        
        // Add all playlists to pending changes
        this.addToPendingChanges(playlists);
      }
    }
  }

  movePlaylistDown(playlistId: number) {
    if (this.orderMode() === 'custom') {
      const playlists = this.filteredPlaylists();
      const currentIndex = playlists.findIndex(p => p.ID === playlistId);
      
      if (currentIndex < playlists.length - 1) {
        // Track the last moved playlist
        this.lastMovedPlaylistId.set(playlistId);
        
        // Swap with next playlist
        const temp = playlists[currentIndex];
        playlists[currentIndex] = playlists[currentIndex + 1];
        playlists[currentIndex + 1] = temp;
        
        // Update OrderNr for all playlists
        playlists.forEach((p, index) => {
          p.OrderNr = index + 1;
        });
        
        // Add all playlists to pending changes
        this.addToPendingChanges(playlists);
      }
    }
  }

  movePlaylistToTop(playlistId: number) {
    if (this.orderMode() === 'custom') {
      const playlists = this.filteredPlaylists();
      const currentIndex = playlists.findIndex(p => p.ID === playlistId);
      
      if (currentIndex > 0) {
        // Track the last moved playlist
        this.lastMovedPlaylistId.set(playlistId);
        
        // Move to top
        const playlist = playlists.splice(currentIndex, 1)[0];
        playlists.unshift(playlist);
        
        // Update OrderNr for all playlists
        playlists.forEach((p, index) => {
          p.OrderNr = index + 1;
        });
        
        // Add all affected playlists to pending changes
        this.addToPendingChanges(playlists.slice(0, currentIndex + 1));
      }
    }
  }

  movePlaylistToBottom(playlistId: number) {
    if (this.orderMode() === 'custom') {
      const playlists = this.filteredPlaylists();
      const currentIndex = playlists.findIndex(p => p.ID === playlistId);
      
      if (currentIndex < playlists.length - 1) {
        // Track the last moved playlist
        this.lastMovedPlaylistId.set(playlistId);
        
        // Move to bottom
        const playlist = playlists.splice(currentIndex, 1)[0];
        playlists.push(playlist);
        
        // Update OrderNr for all playlists
        playlists.forEach((p, index) => {
          p.OrderNr = index + 1;
        });
        
        // Add all affected playlists to pending changes
        this.addToPendingChanges(playlists.slice(currentIndex));
      }
    }
  }

  private addToPendingChanges(playlists: PatientPlaylistModel[]) {
    const currentPending = this.pendingOrderChanges();
    const newPending = [...currentPending];
    
    playlists.forEach(playlist => {
      const existingIndex = newPending.findIndex(p => p.ID === playlist.ID);
      if (existingIndex >= 0) {
        newPending[existingIndex] = playlist;
      } else {
        newPending.push(playlist);
      }
    });
    
    this.pendingOrderChanges.set(newPending);
  }

  private saveAllPendingChanges(): void {
    const pendingChanges = this.pendingOrderChanges();
    
    if (pendingChanges.length === 0) {
      return;
    }

    // Get all playlists (not just filtered ones) to ensure proper order values for all items
    const allPlaylists = this.supervisorHelperService.playlists();
    
    // Create order dictionary with all playlists
    const playlistOrders: { [key: string]: number | null } = {};
    
    // First, set order for all playlists based on their current OrderNr
    allPlaylists.forEach(playlist => {
      if (playlist.OrderNr !== null && playlist.OrderNr !== undefined) {
        playlistOrders[playlist.ID!.toString()] = playlist.OrderNr;
      }
    });
    
    // Then update with the pending changes
    pendingChanges.forEach(playlist => {
      if (playlist.OrderNr !== null && playlist.OrderNr !== undefined) {
        playlistOrders[playlist.ID!.toString()] = playlist.OrderNr;
      }
    });

    // Create the order model
    const orderModel: PlaylistOrderModel = {
      PatientID: Number(this.patientID()),
      PlaylistOrders: playlistOrders
    };

    // Save all order changes in a single call
    this.supervisorApiService.SavePlaylistOrder({
      body: orderModel
    }).subscribe({
      next: () => {
        console.log('All playlist order changes saved successfully');
        // Clear pending changes
        this.pendingOrderChanges.set([]);
        // Trigger a refresh to get the updated data
        this.loadBaseData(Number(this.patientID()));
      },
      error: (error) => {
        console.error('Error saving playlist order changes:', error);
        this.alertService.error('Failed to save playlist order changes');
      }
    });
  }

  private saveOrderModePreference(): void {
    const patientId = this.patientID();
    const orderMode = this.orderMode();
    if (patientId) {
      sessionStorage.setItem(`playlists_order_mode_${patientId}`, orderMode);
    }
  }

  private loadOrderModePreference(): void {
    const patientId = this.patientID();
    if (patientId) {
      const savedOrderMode = sessionStorage.getItem(`playlists_order_mode_${patientId}`) as 'alphabetical' | 'custom';
      if (savedOrderMode && ['alphabetical', 'custom'].includes(savedOrderMode)) {
        this.orderMode.set(savedOrderMode);
      }
    }
  }

  // Selection functionality methods
  toggleIsSelecting(): void {
    this.selectingForDeletion = !this.selectingForDeletion;
  }

  cancelIsSelecting(): void {
    this.selectingForDeletion = false;
    this.playlistIDsForDeletion = [];
  }

  selectAllPlaylists(): void {
    this.doSelectAll = !this.doSelectAll;
    if (this.doSelectAll == false)
      this.playlistIDsForDeletion = [];
  }

  deleteSelectedPlaylists(): void {
    if(this.selectingForDeletion && this.playlistIDsForDeletion.length > 0){
      // First confirmation: Ask about deletion
      this.alertService.confirm(
        'Confirm Deletion',
        `Are you sure you want to mark ${this.playlistIDsForDeletion.length} playlist(s) as deleted?`
      ).subscribe((confirmed) => {
        if (confirmed) {
          this.isLoading = true;
          this.supervisorApiService.MarkMultiplePatientPlaylistsAsDeleted({
            body: {
              PatientID: Number(this.patientID()),
              PlaylistIDs: this.playlistIDsForDeletion
            }
          })
            .subscribe({
              next: (result) => {
                this.alertService.success('Playlists marked as deleted');
                this.supervisorHelperService.triggerUpdateSignal();
                this.cancelIsSelecting();
              },
              complete: () => {
                this.isLoading = false;
              },
              error: (error) => {
                console.error(error);
                this.alertService.error('Failed to mark playlists as deleted');
                this.isLoading = false;
                this.supervisorHelperService.triggerUpdateSignal();
              }
            });
        }
      });
    }
  }

  selectPlaylist(playlistID: number): void {
    if( this.playlistIDsForDeletion.includes(playlistID) ){
      this.playlistIDsForDeletion = this.playlistIDsForDeletion.filter(id => id !== playlistID)
    }
    else {
      this.playlistIDsForDeletion.push(playlistID);
    }
    console.log(this.playlistIDsForDeletion);
  }

  isFirstPlaylist(playlistId: number): boolean {
    return this.filteredPlaylists().findIndex(p => p.ID === playlistId) === 0;
  }

  isLastPlaylist(playlistId: number): boolean {
    return this.filteredPlaylists().findIndex(p => p.ID === playlistId) === this.filteredPlaylists().length - 1;
  }

  hasPendingChanges(playlistId: number): boolean {
    return this.lastMovedPlaylistId() === playlistId;
  }

  getTotalPlaylistsCount(): number {
    return this.filteredPlaylists().length;
  }
}
